import {
  Box,
  Button,
  Card,
  CardContent,
  Link,
  Typography,
} from "@mui/material";
import { useTitle } from "utils/UseTitle";
import {
  handleErrorMessage,
  isTruthy,
  openErrorNotification,
  openSuccessNotification,
} from "helpers/methods";
import { useState } from "react";
import title from "global/constants/TitleConstants";
import { resendAccountVerificationNotification } from "./AuthService";
import CustomLoader from "global/components/CustomLoader/CustomLoader";
import AuthLayout from "layouts/AuthLayout/AuthLayout";
import history from "utils/history";
import viewpaths from "global/constants/ViewPathConstants";
import inkfreeLogo from "assets/images/side_nav_inkfree_logo.svg";
import gmailLogo from "assets/icons1/GmailIcon.svg";
import outlookLogo from "assets/icons1/OutlookIcon.svg";
import yahooLogo from "assets/icons1/YahooIcon.svg";
import { mobile, signer, signerAccentBar } from "theme/colors";
import { authNotice, authRef, refFont } from "./AuthReferenceStyles";

// Mailbox shortcuts, per reference 3a. The reference draws tinted letter
// badges; these use each provider's own mark instead, so the tint and letter
// give way to the logo itself.
const MAIL_PROVIDERS = [
  {
    label: "Open Gmail",
    logo: gmailLogo,
    url: "https://mail.google.com/",
  },
  {
    label: "Open Outlook",
    logo: outlookLogo,
    url: "https://outlook.live.com/",
  },
  {
    label: "Open Yahoo",
    logo: yahooLogo,
    url: "https://mail.yahoo.com/",
  },
];

const RegistrationSuccessModal = (props: { location: any }) => {
  useTitle(title.CHECK_YOUR_EMAIL);
  const email = props.location?.state?.email;
  const [isLoading, setIsLoading] = useState<boolean>();

  const resendAccountVerificationEmail = async () => {
    try {
      setIsLoading(true);
      if (!isTruthy(email)) {
        openErrorNotification("Kindly use the original link for activation!");
        return;
      } else {
        await resendAccountVerificationNotification(email);
        openSuccessNotification("Account activation email sent successfully!");
      }
    } catch (error: any) {
      handleErrorMessage(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenMailProvider = (url: string) => {
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const renderMailButton = (provider: (typeof MAIL_PROVIDERS)[number]) => (
    <Button
      key={provider.label}
      variant="outlined"
      onClick={() => handleOpenMailProvider(provider.url)}
      sx={{
        flex: 1,
        // Three equal thirds of the card leave ~135px per button, which is
        // under what MUI's default 15px side padding needs for "Open Outlook"
        // — without this the longest label wraps and that button alone grows a
        // second line, throwing the row out of alignment.
        minWidth: 0,
        px: 1,
        height: "44px",
        gap: 1,
        whiteSpace: "nowrap",
        lineHeight: 1,
        borderRadius: "8px",
        borderColor: authRef.inputBorder,
        backgroundColor: "background.paper",
        color: mobile.base[500],
        fontWeight: 600,
        fontSize: refFont.link,
        textTransform: "none",
        "&:hover": {
          borderColor: authRef.inputBorderHover,
          backgroundColor: "background.paper",
        },
      }}
      aria-label={`${provider.label} in a new tab`}
    >
      <Box
        component="img"
        src={provider.logo}
        alt=""
        aria-hidden="true"
        sx={{
          width: 20,
          height: 20,
          flexShrink: 0,
          objectFit: "contain",
          display: "block",
        }}
      />
      {provider.label}
    </Button>
  );

  return (
    <AuthLayout>
      {/* Brand accent bar across the top of the canvas, per reference 3a. */}
      <Box
        aria-hidden="true"
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          height: "5px",
          background: signerAccentBar,
          zIndex: (theme) => theme.zIndex.appBar,
        }}
      />
      <Card
        elevation={0}
        sx={{
          width: "100%",
          maxWidth: 520,
          mx: "auto",
          borderRadius: "16px",
          border: `1px solid ${authRef.cardBorder}`,
          boxShadow: authRef.cardShadow,
          padding: { xs: "28px 24px 24px", sm: "40px 48px 36px" },
          boxSizing: "border-box",
        }}
      >
        <CardContent
          sx={{
            p: 0,
            "&:last-child": { pb: 0 },
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Box
            component="img"
            src={inkfreeLogo}
            alt="Inkfree e-Sign"
            sx={{ width: 106, height: "auto", display: "block" }}
          />

          <Box
            aria-hidden="true"
            sx={{
              width: 64,
              height: 64,
              mt: 3.25,
              borderRadius: "50%",
              background: "linear-gradient(135deg, #FDF1EA, #F4EDFB)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke={signer.orange.main}
              strokeWidth="1.7"
              style={{ width: 28, height: 28 }}
            >
              <rect x="3" y="5" width="18" height="14" rx="2" />
              <path d="M3 7l9 6 9-6" />
            </svg>
          </Box>

          <Typography
            component="h1"
            sx={{
              mt: 2.25,
              color: mobile.base[500],
              fontSize: refFont.cardHeading,
              fontWeight: 700,
              textAlign: "center",
            }}
          >
            You've got mail (from us)
          </Typography>

          <Typography
            sx={{
              mt: 1.25,
              color: authRef.subText,
              fontSize: refFont.body,
              lineHeight: 1.6,
              textAlign: "center",
            }}
          >
            We sent a verification link to
            <br />
            <Box
              component="span"
              sx={{ fontWeight: 700, color: mobile.base[500] }}
            >
              {email || "your email address"}
            </Box>
            <br />
            Click it and you're in.
          </Typography>

          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              gap: 1.25,
              mt: 3.25,
              width: "100%",
            }}
          >
            {MAIL_PROVIDERS.map(renderMailButton)}
          </Box>

          <Box
            sx={{
              width: "100%",
              boxSizing: "border-box",
              mt: 3,
              padding: "16px 18px",
              border: `1.5px dashed ${authNotice.border}`,
              backgroundColor: authNotice.bg,
              borderRadius: "10px",
              display: "flex",
              gap: 1.5,
              alignItems: "flex-start",
            }}
          >
            <svg
              aria-hidden="true"
              viewBox="0 0 24 24"
              fill="none"
              stroke={authNotice.icon}
              strokeWidth="1.8"
              style={{ width: 20, height: 20, flexShrink: 0, marginTop: 1 }}
            >
              <path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z" />
              <path d="M12 15l-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z" />
            </svg>
            <Typography
              sx={{
                color: authNotice.text,
                fontSize: refFont.link,
                lineHeight: 1.65,
              }}
            >
              <Box
                component="span"
                sx={{ fontWeight: 700, color: authNotice.strong }}
              >
                Nothing yet?
              </Box>{" "}
              Peek in your{" "}
              <Box component="span" sx={{ fontWeight: 700 }}>
                Spam folder
              </Box>{" "}
              — our email occasionally gets seated next to the "you've won a
              yacht" crowd. Rescue it and hit{" "}
              <Box component="span" sx={{ fontWeight: 700 }}>
                "Not spam"
              </Box>{" "}
              so it never happens again.
            </Typography>
          </Box>

          <Typography
            sx={{
              mt: 2.75,
              color: authRef.subText,
              fontSize: refFont.label,
              textAlign: "center",
            }}
          >
            Still nothing?{" "}
            <Link
              component="button"
              onClick={resendAccountVerificationEmail}
              disabled={isLoading}
              sx={{
                color: signer.orange.main,
                fontSize: "inherit",
                fontWeight: 700,
                textDecoration: "none",
                verticalAlign: "baseline",
                "&:hover": { textDecoration: "underline" },
                "&:disabled": {
                  color: "text.disabled",
                  cursor: "not-allowed",
                },
              }}
              aria-label="Resend account verification email"
              tabIndex={0}
              onKeyPress={(e) => {
                if (e.key === "Enter" || e.key === " ")
                  resendAccountVerificationEmail();
              }}
            >
              Resend email
            </Link>
          </Typography>
        </CardContent>
      </Card>
      <CustomLoader isLoading={isLoading} />
    </AuthLayout>
  );
};
export default RegistrationSuccessModal;
